/* eslint-disable max-lines */
import Nprogress from "nprogress";
import VueNotifications from "vue-notifications";
import ConfidenceLevel from "@/constants/ConfidenceLevel";
import GoalType from "@/constants/GoalType";
import { i18n } from "@/i18n";
import groupBy from "lodash/groupBy";
import debugWrapper from "@/helpers/debugWrapper";

import uuid from "uuid/v4";
import { goalAPI, teamAPI } from "@/api";
import {
	GoalCreatedEvent,
	GoalUpdatedEvent,
	GoalDeletedEvent,
	GoalArchivedEvent,
	GoalUnarchivedEvent,
	KeyResultCreatedEvent,
	KeyResultUpdatedEvent,
	KeyResultDeletedEvent,
	KeyResultConfidenceUpdatedEvent,
	KeyResultMeasureUpdatedEvent,
	GoalConfidenceUpdatedEvent
} from "@/telemetry";

const goalAPIFetchers = {
	[ GoalType.ACTIVE ]: goalAPI.myActiveGoals,
	[ GoalType.DRAFT ]: goalAPI.myDraftGoals,
	[ GoalType.ARCHIVED ]: goalAPI.myArchivedGoals,
	default: goalAPI.myActiveGoals,
};

export default debugWrapper( "teaming:store:goals:actions", {
	archiveGoal: async ( { state, commit, rootState, rootGetters, dispatch, }, { goalId, } ) => {
		try {
			Nprogress.start();
			const goal = state.myGoals.find( g => g.id === goalId );
			if ( !goal.archived ) {
				const timezone = rootGetters[ "auth/timezone" ];
				const editedGoal = {
					...goal,
					archived: true, // converted to bool in API
				};
				const archivedGoal = await goalAPI.saveGoal( { editedGoal, timezone, } );
				const goalTeam = rootState.apiTeams.myTeams.find( myTeam => myTeam.id === goal.teamId ) || {};
				dispatch( "fetchGokrMetadata" );
				const event = new GoalArchivedEvent( goalTeam.name );
				event.submit();
				commit( "goalSaved", archivedGoal );
				VueNotifications.success( {
					message: i18n.t( "gokr.GoalDialog_archiveSuccessMessage" ),
				} );
			}
		} catch ( e ) {
			VueNotifications.error( {
				message: i18n.t( "gokr.GoalDialog_archiveErrorMessage", { error: e.message || "unknown error", } ),
			} );
			console.error( e );
		}
		Nprogress.done();
	},
	changeTeamIdFilter: async ( { commit, dispatch, state, }, teamId ) => {
		const goalType = state.selectedGoalType;
		const sortDirection = state.sortDirection;
		commit( "clearGoals" );
		commit( "setSelectedGoalType", { goalType, suppressTelemetry: true, } );
		commit( "setSortDirection", sortDirection );
		commit( "setSelectedTeamId", teamId );
		dispatch( "fetchGoalsAndMetadata", { forceRefresh: true, } );
	},
	changeGoalSortDirection: async ( { commit, dispatch, state, }, sortDirection ) => {
		const goalType = state.selectedGoalType;
		const selectedTeamId = state.selectedTeamId;
		commit( "clearGoals" );
		commit( "setSelectedGoalType", { goalType, suppressTelemetry: true, } );
		commit( "setSortDirection", sortDirection );
		commit( "setSelectedTeamId", selectedTeamId );
		dispatch( "fetchGoalsAndMetadata", { forceRefresh: true, } );
	},
	changeSelectedGoalType: async ( { commit, dispatch, state, }, { goalType, suppressTelemetry, } ) => {
		const selectedTeamId = state.selectedTeamId;
		const sortDirection = state.sortDirection;
		commit( "clearGoals" );
		commit( "setSelectedGoalType", { goalType, suppressTelemetry, } );
		commit( "setSortDirection", sortDirection );
		commit( "setSelectedTeamId", selectedTeamId );
		dispatch( "fetchGoalsAndMetadata", { forceRefresh: true, } );
	},
	deleteGoal: async ( { commit, state, rootState, dispatch, }, { goalId, } ) => {
		try {
			Nprogress.start();
			await goalAPI.deleteGoal( { goalId, } );
			const goal = state.myGoals.find( myGoal => myGoal.id === goalId ) || {};
			const goalTeam = rootState.apiTeams.myTeams.find( myTeam => myTeam.id === goal.teamId ) || {};
			dispatch( "fetchGokrMetadata" );
			const event = new GoalDeletedEvent( goalTeam.name );
			event.submit();
			commit( "goalDeleted", goalId );
			VueNotifications.success( {
				message: i18n.t( "gokr.GoalDialog_deleteSuccessMessage" ),
			} );
		} catch ( e ) {
			VueNotifications.error( {
				message: i18n.t( "gokr.GoalDialog_deleteErrorMessage", { error: e.message || "unknown error", } ),
			} );
			console.error( e );
		}
		Nprogress.done();
	},
	editGoal: async ( { state, commit, }, goalId ) => {
		try {
			Nprogress.start();
			let keyResults = state.keyResultsMap[ goalId ];
			if ( !keyResults ) {
				keyResults = await goalAPI.fetchKeyResults( { goalId, } );
				commit( "keyResultsFetched", { [ goalId ]: keyResults, } );
			}
			commit( "editingGoal", { goalId, keyResults, } );
		} catch ( e ) {
			VueNotifications.error( {
				message: i18n.t( "gokr.GoalDialog_fetchKeyResultsErrorMessage", { error: e.message || "unknown error", } ),
			} );
			console.error( e );
		}
		Nprogress.done();
	},
	examineGoal: ( { commit, }, goalId ) => {
		commit( "hideAllKeyResults" );
		commit( "toggleKeyResultVisibility", { goalId, isVisible: true, } );
		commit( "goalExamined", goalId );
	},
	// eslint-disable-next-line max-statements
	fetchGoals: async ( { commit, state, }, options = {} ) => {
		commit( "toggleIsLoadingGoals", true );
		let wasSuccessful = true;
		try {
			Nprogress.start();
			const goalType = state.selectedGoalType;
			const hasGoalTypeCounts = state.initialMetadata.goalCounts[ state.selectedGoalType ];

			let myGoals = [];

			// only fetch if the initial metadata indicates there *are* goals for this type
			// (or if we're forcing it)
			if ( !( options.forceRefresh || hasGoalTypeCounts ) ) {
				commit( "myGoalsLoaded", { goalType, myGoals, } );
			} else {
				options.offset = state.currentOffset;
				options.limit = state.currentLimit;
				options.sortDirection = state.sortDirection;
				options.teamId = state.selectedTeamId;
				options.meetingId = state.selectedMeetingId;
				options.goalType = [ state.selectedGoalType, ];
				const fetchOperation =
					goalAPIFetchers[ goalType ] ||
					goalAPIFetchers.default;
				myGoals = await fetchOperation( options );
				if ( myGoals.length ) {
					commit( "incrementPagingOffset" );
				} else {
					commit( "setNoMoreGoalsForType" );
				}
				commit( "myGoalsLoaded", { goalType, myGoals, } );

				const goalIds = myGoals.reduce( ( accum, goal ) => {
					if ( goal.keyResultSummary.keyResultCount > 0 ) {
						accum.push( goal.id );
					}
					return accum;
				}, [] );

				commit( "toggleKeyResultsBeingFetched", { goalIds, inProgress: true, } );

				const krParams = {
					goalIds,
					meetingId: state.selectedMeetingId,
				};

				// meeting ID should not be sent if it is ever falsy
				if ( !krParams.meetingId ) {
					delete krParams.meetingId;
				}

				goalAPI.searchKeyResults( krParams ).then( keyResults => {
					const keyResultMap = groupBy( keyResults, "goalId" );
					commit( "keyResultsFetched", keyResultMap );
					commit( "toggleKeyResultsBeingFetched", { goalIds, inProgress: false, } );
				}, e => {
					commit( "keyResultFetchFailed", { goalIds, message: e.message, } );
					commit( "toggleKeyResultsBeingFetched", { goalIds, inProgress: false, } );
				} );
			}

			commit( "setInitialLoadComplete" );
		} catch ( e ) {
			wasSuccessful = false;
			VueNotifications.error( {
				message: `Unexpected error fetching goals: ${e.message || "unknown error"}.`,
			} );
			console.error( e );
		}
		Nprogress.done();
		commit( "toggleIsLoadingGoals", false );
		return wasSuccessful;
	},
	// This is a "coordinating action" that handles multiple actions in this store
	fetchGoalsAndMetadata: async ( { dispatch, }, options = {} ) => {
		await Promise.all( [
			dispatch( "fetchGokrMetadata" ),
			dispatch( "fetchGoals", options ),
		] );
	},
	fetchGokrMetadata: async ( { commit, } ) => {
		const initialMetadata = await goalAPI.fetchMetadata();
		commit( "gokrMetadataLoaded", { initialMetadata, } );
	},
	fetchKeyResults: async ( { commit, }, goalId ) => {
		try {
			Nprogress.start();
			commit( "toggleKeyResultsBeingFetched", { goalIds: [ goalId, ], inProgress: true, } );
			const keyResults = await goalAPI.fetchKeyResults( { goalId, } );
			commit( "keyResultsFetched", { [ goalId ]: keyResults, } );
		} catch ( e ) {
			VueNotifications.error( {
				message: i18n.t( "gokr.GoalDialog_fetchKeyResultsErrorMessage", { error: e.message || "unknown error", } ),
			} );
			console.error( e );
		}
		commit( "toggleKeyResultsBeingFetched", { goalIds: [ goalId, ], inProgress: false, } );
		Nprogress.done();
	},
	// TODO: Convert this to use fetchGoals
	fetchOneOnOneGoals: async ( { commit, /* rootState, rootGetters, */ }, { teamId, } ) => {
		commit( "toggleIsLoadingGoals", true );
		let wasSuccessful = true;
		Nprogress.start();
		try {
			const goalType = GoalType.ACTIVE;
			let oneOnOneGoals = [];
			oneOnOneGoals = await goalAPI.fetchTeamGoals( {
				teamId,
				goalTypes: [
					GoalType.ACTIVE,
					GoalType.DRAFT,
					GoalType.ARCHIVED,
				],
			} );
			commit( "myGoalsLoaded", { goalType, myGoals: oneOnOneGoals, } );

			const goalIds = oneOnOneGoals.filter( goal => {
				const keyResultMetadata = goal.keyResultSummary || {};
				const keyResultCount = keyResultMetadata.keyResultCount || 0;
				return keyResultCount > 0;
			} ).map( goal => goal.id );

			if ( goalIds.length ) {
				commit( "toggleKeyResultsBeingFetched", { goalIds, inProgress: true, } );

				goalAPI.searchKeyResults( { goalIds, teamId, } ).then( keyResults => {
					const keyResultMap = groupBy( keyResults, "goalId" );
					commit( "keyResultsFetched", keyResultMap );
					commit( "toggleKeyResultsBeingFetched", { goalIds, inProgress: false, } );
				}, e => {
					commit( "keyResultFetchFailed", { goalIds, message: e.message, } );
					commit( "toggleKeyResultsBeingFetched", { goalIds, inProgress: false, } );
				} );
			}

			commit( "setInitialLoadComplete", {} );
		} catch ( e ) {
			wasSuccessful = false;
			const message = i18n.t( "scheduleStore.store_loadMeetingGoalsError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}
		Nprogress.done();
		commit( "toggleIsLoadingGoals", false );
		return wasSuccessful;
	},
	processGoalDeletedNotification: ( { commit, }, { goalId, } ) => {
		commit( "goalDeleted", goalId );
	},
	processGoalUpdate: async ( { commit, /* rootState, rootGetters, */ }, { goalId, } ) => {
		let wasSuccessful = true;
		Nprogress.start();
		try {
			const goal = await goalAPI.fetchGoal( { goalId, } );
			const keyResults = goal.keyResults || {};
			delete goal.keyResults;
			commit( "updatedGoalLoaded", { goal, } );
			commit( "keyResultsFetched", { [ goalId ]: keyResults, } );
		} catch ( e ) {
			wasSuccessful = false;
			const message = i18n.t( "scheduleStore.store_loadMeetingGoalsError", {
				message: e.message,
			} );
			VueNotifications.error( {
				message,
			} );
			console.error( message, e );
		}
		Nprogress.done();
		commit( "toggleIsLoadingGoals", false );
		return wasSuccessful;
	},
	processKeyResultDeletedNotification: ( { commit, }, { goalId, keyResultId, } ) => {
		commit( "keyResultDeleted", { goalId, keyResultId, } );
	},
	saveGoal: async ( { commit, dispatch, state, rootState, rootGetters, }, { goal, trigger = "", } ) => {
		commit( "toggleSavingGoal", true );

		const goalTeam = rootState.apiTeams.myTeams.find( myTeam => myTeam.id === goal.teamId ) || {};

		const execSaveGoal = async editedGoal => {
			const isNew = !editedGoal.id;
			const timezone = rootGetters[ "auth/timezone" ];
			const savedGoal = await goalAPI.saveGoal( { editedGoal, timezone, } );
			dispatch( "fetchGokrMetadata" );
			let event = isNew ?
				new GoalCreatedEvent( goalTeam.name ) :
				new GoalUpdatedEvent( goalTeam.name );
			event.submit();
			if ( trigger === "confidence" ) {
				event = new GoalConfidenceUpdatedEvent( goalTeam.name );
				event.submit();
			}

			return savedGoal;
		};

		const saveKeyResults = async goalId => {
			const uiKeys = [];
			const editedKeyResults = [];

			Object.keys( state.editingKeyResultsMap ).forEach( ( uiKey, index ) => {
				const keyResult = state.editingKeyResultsMap[ uiKey ];
				const keyResultToSave = { ...keyResult, };
				keyResultToSave.confidence = ( keyResultToSave.confidence || ConfidenceLevel.HIGH );
				keyResultToSave.sortOrder = index;
				uiKeys.push( uiKey );
				editedKeyResults.push( keyResultToSave );
			} );

			const savedKeyResults = await goalAPI.saveKeyResults( { goalId, editedKeyResults, } );

			editedKeyResults.forEach( editedKeyResult => {
				const isNew = !editedKeyResult.id;
				const event = isNew ?
					new KeyResultCreatedEvent( goalTeam.name ) :
					new KeyResultUpdatedEvent( goalTeam.name );
				event.submit();
			} );

			// return savedKeyResults;
			return () => {
				savedKeyResults.forEach( savedKeyResult => {
					commit( "keyResultSaved", { uiKey: uiKeys.shift(), keyResult: savedKeyResult, } );
				} );
			};
		};

		const deleteKeyResults = async () => {
			await Promise.all( state.deletingKeyResultsList.map( keyResultId => {
				return goalAPI.deleteKeyResult( { goalId: goal.id, keyResultId, } )
					.then( () => {
						const event = new KeyResultDeletedEvent( goalTeam.name );
						event.submit();
						commit( "keyResultDeleted", { goalId: goal.id, keyResultId, } );
					} );
			} ) );
		};

		let wasSuccessful = true;
		try {
			Nprogress.start();
			const savedGoal = await execSaveGoal( goal );
			await deleteKeyResults();
			const hasKeyResults = ( state.editingKeyResultsOrder.length > 0 );
			let commitKeyResults = () => {};
			if ( hasKeyResults ) {
				commitKeyResults = await saveKeyResults( savedGoal.id );
			}

			// We need to load the goal again because the KRs
			// aren't persisted until *after* the goal is, which
			// means the keyResultSummary information on the goal
			// is stale when it comes back from the API on save.
			// Re-fetching ensures we have correct KR summary metrics.
			const refreshedGoal = await goalAPI.fetchGoal( { goalId: savedGoal.id, } );
			commit( "goalSaved", refreshedGoal );
			commitKeyResults();

			VueNotifications.success( {
				message: hasKeyResults ?
					i18n.t( "gokr.GoalDialog_saveWithKeyResultsSuccessMessage" ) :
					i18n.t( "gokr.GoalDialog_saveSuccessMessage" ),
			} );
		} catch ( e ) {
			wasSuccessful = false;
			VueNotifications.error( {
				message: i18n.t( "gokr.GoalDialog_saveErrorMessage", { error: e.message || "unknown error", } ),
			} );
			console.error( e );
		}
		Nprogress.done();
		commit( "toggleSavingGoal", false );
		return wasSuccessful;
	},
	selectGoalTeam: async ( { commit, }, teamId ) => {
		commit( "setIsSelectedGoalTeamChanging", true );
		if ( teamId === null ) {
			commit( "goalTeamSelected", {} );
			return commit( "setIsSelectedGoalTeamChanging", false );
		}
		let fetchedTeam = null;
		try {
			Nprogress.start();
			fetchedTeam = await teamAPI.fetchTeam( { teamId, } );
			commit( "goalTeamSelected", fetchedTeam );
		} catch ( e ) {
			VueNotifications.error( {
				message: i18n.t( "gokr.GoalDialog_fetchTeamErrorMessage", { error: e.message || "unknown error", } ),
			} );
			console.error( e );
		}
		Nprogress.done();
		commit( "setIsSelectedGoalTeamChanging", false );
	},
	setTeamFilter: ( { commit, }, { teamId, } ) => {
		commit( "setTeamFilter", teamId );
	},
	unarchiveGoal: async ( { state, commit, rootState, rootGetters, dispatch, }, goalId ) => {
		try {
			Nprogress.start();
			const goal = state.myGoals.find( g => g.id === goalId );
			if ( goal.archived ) {
				const editedGoal = {
					...goal,
					archived: false,
				};
				const timezone = rootGetters[ "auth/timezone" ];
				const archivedGoal = await goalAPI.saveGoal( { editedGoal, timezone, } );
				const goalTeam = rootState.apiTeams.myTeams.find( myTeam => myTeam.id === goal.teamId ) || {};
				dispatch( "fetchGokrMetadata" );
				const event = new GoalUnarchivedEvent( goalTeam.name );
				event.submit();
				commit( "goalSaved", archivedGoal );
				VueNotifications.success( {
					message: i18n.t( "gokr.GoalDialog_unarchiveSuccessMessage" ),
				} );
			}
		} catch ( e ) {
			VueNotifications.error( {
				message: i18n.t( "gokr.GoalDialog_unarchiveErrorMessage", { error: e.message || "unknown error", } ),
			} );
			console.error( e );
		}
		Nprogress.done();
	},
	updateInlineKeyResult: async ( { commit, state, rootState, }, { editedKeyResult, trigger = "", } ) => {
		const { goalId, } = editedKeyResult;
		let wasSuccessful = true;
		Nprogress.start();
		try {
			const savedKeyResult = await goalAPI.saveKeyResult( {
				goalId,
				editedKeyResult,
			} );
			const goal = state.myGoals.find( myGoal => myGoal.id === editedKeyResult.goalId ) || {};
			const goalTeam = rootState.apiTeams.myTeams.find( myTeam => myTeam.id === goal.teamId ) || {};
			let event = null;
			if ( trigger === "confidence" ) {
				event = new KeyResultConfidenceUpdatedEvent( goalTeam.name );
			}
			if ( trigger === "progress" ) {
				event = new KeyResultMeasureUpdatedEvent( goalTeam.name );
			}
			if ( event ) {
				event.submit();
			}
			commit( "keyResultSaved", { uiKey: uuid(), keyResult: savedKeyResult, } );
			const keyResultGoal = await goalAPI.fetchGoal( { goalId, } );
			commit( "goalSaved", keyResultGoal );
			VueNotifications.success( {
				message: i18n.t( "gokr.GoalDialog_updateInlineKeyResultSuccess" ),
			} );
		} catch ( e ) {
			wasSuccessful = false;
			VueNotifications.error( {
				message: i18n.t( "gokr.GoalDialog_updateInlineKeyResultError", {
					error: e.message || i18n.t( "gokr.GoalDialog_unknownError" ),
				} ),
			} );
			console.error( e );
		}
		Nprogress.done();
		return wasSuccessful;
	},
}, { skipFirstArg: true, } );



// WEBPACK FOOTER //
// ./src/store/modules/goals/actions.js